import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import mapboxgl from "mapbox-gl";
import MapboxDirections from "@mapbox/mapbox-gl-directions/dist/mapbox-gl-directions";
import "@mapbox/mapbox-gl-directions/dist/mapbox-gl-directions.css";
import "mapbox-gl/dist/mapbox-gl.css";

import {
  mapboxToken,
  mapboxOwner,
  terrainId,
  satelliteId,
} from "../../configuration";

import {
  capitalize,
  returnPopups,
  popUpAndIndicator,
  addAccuracyCircle,
} from "../../global/utility/Utils";
import {
  setActiveTabGlobal,
  setShipmentDataGlobal,
  setShipmentStatusGlobal,
  setShipmentTitlesForFilter,
  setShipmentTitlesForHighlight,
} from "../../store/actions/shipment";
import { Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { IconButton } from "@mui/material";
import {
  updateMap,
  updateIsLoading,
  fetchLocationAlertsAction,
  updateFilter,
  setTrackDataLoaded
} from "../../store/actions/shipment";
import { addButtons } from "./CreateShipments/map-utils";
import getAddressfromLatLong from "../../global/utility/getAddressfromLatLong";

import Fullscreen from "../../assets/fullscreen.png";
import ExitFullscreen from "../../assets/exit_fullscreen.png";

function ShipmentMap() {
  const isLoadingMap = useSelector((state) => state.shipment.isLoading.map);
  const isMapFullScreen = useSelector(
    (state) => state.shipment.data.map.isFullScreen
  );
  const mapData = useSelector((state) => state.shipment.data.map);
  const shipments = useSelector((state) => state.shipment.data.allShipments);
  const shipmentsSelected = useSelector(
    (state) => state.shipment.ui.selectedFields
  );
  const selectedDeviceDetails = useSelector(
    (state) => state.shipment.ui.selectedDeviceDetails
  );
  const [intermediateLocations, setIntermediateLocations] = useState([]);
  const [shipmentData, setShipmentData] = useState({});
  const pathPoints = useSelector((state) => state.shipment.shipmentTrackData);
  const trackDataLoaded = useSelector(state => state.shipment.trackDataLoaded);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markRef = useRef(null);
  const fullScreenContainer = useRef();
  const validateCoord = (coord) => {
    if (coord && Array.isArray(coord)) {
      if (!isNaN(coord[0]) && !isNaN(coord[1])) {
        if (
          coord[0] >= -180 &&
          coord[0] <= 180 &&
          coord[1] >= -90 &&
          coord[1] <= 90
        ) {
          return true;
        }
        return false;
      }
      return false;
    } else {
      return false;
    }
  };

  useEffect(() => {
    return () => {
      dispatch(setTrackDataLoaded())
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        dispatch(updateMap({ isFullScreen: false }));
        return;
      }

      dispatch(updateMap({ isFullScreen: !isMapFullScreen }));
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange); // For Safari/older browsers
    document.addEventListener("mozfullscreenchange", handleFullscreenChange); // For Firefox
    document.addEventListener("MSFullscreenChange", handleFullscreenChange); // For IE/Edge

    // Cleanup: remove event listeners when the component unmounts
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange
      );
    };
  }, [dispatch]);

  useEffect(() => {
    if (shipmentsSelected.length === 1) {
      dispatch(updateIsLoading(true));
    }
    //shipment data initialization from location props
    const shipmentData = shipmentsSelected[0]?.record;
    if (!shipmentData) return;

    const intermediateLocations =
      shipmentData?.deviceprofile?.locations?.filter(
        (loc) =>
          !loc?.locationtype?.includes("Destination") &&
          !loc?.locationtype?.includes("Origin")
      );

    setIntermediateLocations(
      intermediateLocations ? intermediateLocations : [0, 0]
    );

    setShipmentData({
      ...shipmentData,
      originLocation: shipmentData?.deviceprofile?.locations?.find(
        (loc) => loc.locationtype === "Origin"
      ),
      destinationLocation: shipmentData?.deviceprofile?.locations?.find(
        (loc) => loc.locationtype === "Destination"
      ),
      intermediateLocations: intermediateLocations,
    });
  }, [shipmentsSelected]);

  useEffect(() => {
    if (!shipmentData || shipmentsSelected?.length !== 1) return;
    const getData = async () => {
      if (!shipmentData?.createdon) return;
      try {
        let value = {
          startTime: shipmentData?.createdon,
          endTime: shipmentData?.updatedat,
          deviceId: shipmentData?.deviceid,
          shipmentId: shipmentData?.shipmentid,
          originCoord: [
            shipmentData?.originLocation?.longitude || 0,
            shipmentData?.originLocation?.latitude || 0,
          ],
          alertType : shipmentData?.originLocation?.alerttype || "No Alert",
        };
        dispatch(fetchLocationAlertsAction(value));
        dispatch(updateIsLoading(false));
      } catch (error) {
        dispatch(updateIsLoading(false));
        console.log(error);
      }
    };
    getData();
  }, [shipmentData]);

  useEffect(() => {
    // if (!Array.isArray(shipments) || shipments.length === 0) return;
    if(shipmentsSelected.length === 1 && !trackDataLoaded) return;
    if (!mapboxgl.supported()) {
      console.error("WebGL is not supported in this browser. Map cannot be initialized.");
      return;
    }
    if (!Array.isArray(shipments) || shipments.length === 0 ) {
      const nav = new mapboxgl.NavigationControl({
        visualizePitch: true,
      });
      try {
        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: `mapbox://styles/${mapboxOwner}/${terrainId}`,
          center: [-98.5795, 39.8283], // Default center coordinates
          zoom: 4.5, // Default zoom level
        });
      } catch (e) {
        console.error("Failed to initialize map:", e);
        return;
      }
      map.current.addControl(nav, "bottom-right");

      // adds sattelite and Terrain buttons to map
      addButtons(map.current, terrainId, satelliteId, mapboxOwner);

      return () => {
        if (map.current) map.current.remove();
      };
    }

    if (shipmentsSelected.length === 1) {
      if (!shipmentData) return;

      const originCoordinates = [
        shipmentData?.originLocation?.longitude || 0,
        shipmentData?.originLocation?.latitude || 0,
      ];
      const destinationCoordinates = [
        shipmentData?.destinationLocation?.longitude || 0,
        shipmentData?.destinationLocation?.latitude || 0,
      ];

      const nav = new mapboxgl.NavigationControl({
        visualizePitch: true,
      });
      try {
        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: `mapbox://styles/${mapboxOwner}/${terrainId}`,
          center: validateCoord(originCoordinates) ? originCoordinates : [0, 0],
          zoom: 9,
        });
      } catch (e) {
        console.error("Failed to initialize map:", e);
        return;
      }
      map.current.addControl(nav, "bottom-right");

      // adds sattelite and Terrain buttons to map
      addButtons(map.current, terrainId, satelliteId, mapboxOwner);

      if (!map?.current) return;

      if (validateCoord(originCoordinates)) {
        const popupstr = returnPopups({
          loc_type: "Origin",
          Address: shipmentData?.originLocation?.address,
          locationName: shipmentData?.originLocation?.name,
        });
        const indicator = `<div class="shipment-map-indicator-container">
              <img src="/assets/map-Indicators.svg" style="width:20px; height:20px" alt="Start Marker"/>
        </div>`
        popUpAndIndicator(originCoordinates, popupstr, indicator, map?.current);
      }

      //Intermediate location markers
      for (let i = 0; i < intermediateLocations?.length; i++) {
        const loc = intermediateLocations[i];
        const coords = [loc?.longitude, loc?.latitude];
        if (validateCoord(coords)) {
          const popupstr = returnPopups({
            loc_type: loc?.locationtype,
            Address: loc?.address,
            locationName: loc?.name,
          });
          const indicator = `<div class="shipment-map-indicator-container">
                               <div style="width:15px; height:15px; background-color:white;
                               border-radius:50%; border:2px solid black;" alt="End Marker"/>
                           </div>`;
          popUpAndIndicator(
            coords,
            popupstr,
            indicator,
            map?.current,
            "bottom"
          );
        }
      }

      if (pathPoints && Array.isArray(pathPoints)) {
        // plots the path from device coord points
        const directions = new MapboxDirections({
          accessToken: mapboxToken,
          unit: "metric",
          profile: "mapbox/driving",
          interactive: false,
          controls: false,
        });

        map.current.addControl(directions, "top-left");

        // Map initial load
        map.current.on("load", () => {
          let bounds = pathPoints?.reduce((bounds, point) => {
            const coords = point.coords;
            return validateCoord(coords) ? bounds.extend(coords) : bounds;
          }, new mapboxgl.LngLatBounds());

          //extend bounds to origin coords and destination coords
          bounds = validateCoord(originCoordinates)
            ? bounds.extend(originCoordinates)
            : bounds;
          bounds = validateCoord(destinationCoordinates)
            ? bounds.extend(destinationCoordinates)
            : bounds;
          intermediateLocations.forEach((loc) => {
            const coord = [loc?.longitude, loc?.latitude];
            bounds = validateCoord(coord) ? bounds.extend(coord) : bounds;
          });

          if (shipmentData?.shipmentstate?.toLowerCase() != "completed") {
            const currentLoc = [
              shipmentData?.currentlocation?.long,
              shipmentData?.currentlocation?.lat,
            ];
            bounds = validateCoord(currentLoc)
              ? bounds.extend(currentLoc)
              : bounds;
          }
          const sw = [bounds?._sw?.lng, bounds?._sw?.lat];
          const ne = [bounds?._ne?.lng, bounds?._ne?.lat];

          if (validateCoord(sw) && validateCoord(ne)) {
            map.current.fitBounds(bounds, { padding: 90 });
          }

          //remove layer and source before adding
          if (map.current?.getLayer("LineStringLayer-layerl")) {
            map.current.removeLayer("LineStringLayer-layerl");
          }
          if (map.current?.getLayer("dotted-line-layerl")) {
            map.current.removeLayer("dotted-line-layerl");
          }
          if (map.current?.getSource("LineString-sourcel")) {
            map.current.removeSource("LineString-sourcel");
          }
          if (map.current?.getSource("dotted-line-sourcel")) {
            map.current.removeSource("dotted-line-sourcel");
          }

          //Draws solid line using location pts from eventsmanager/search api
          const points = pathPoints
            ?.map((point) => point.coords)
            .filter((coords) => validateCoord(coords));
          map?.current?.addSource("LineString-sourcel", {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: [
                {
                  type: "Feature",
                  geometry: {
                    type: "LineString",
                    coordinates: [...points],
                  },
                  properties: {},
                },
              ],
            },
          });

          map.current.addLayer({
            id: "LineStringLayer-layerl",
            type: "line",
            source: "LineString-sourcel",
            layout: {
              "line-join": "round",
              "line-cap": "round",
            },
            paint: {
              "line-color": "#000000",
              "line-width": 2,
            },
          });

          let { coords, accuracy } = pathPoints[pathPoints.length - 1] || {
            coords: [0, 0],
            accuracy: 0,
          };

          if (pathPoints?.length < 1) {
            if (validateCoord(originCoordinates)) {
              coords = originCoordinates;
              accuracy = 0;
            }
          }

          if (validateCoord(destinationCoordinates)) {
            const popupstr = returnPopups({
              loc_type: "Destination",
              Address: shipmentData?.destinationLocation?.address,
              locationName: shipmentData?.destinationLocation?.name,
            });
            const indicator = `<div class="shipment-map-indicator-container">
                             <img src="/assets/destination-location.svg" style="width:35px; height:35px; position: absolute; top: -20px; left: -7px; z-index:999" alt="Start Marker"/>
                             <img src="/assets/map-Indicators.svg" style="width:20px; height:20px" alt="Start Marker"/>
                           </div>`;
            popUpAndIndicator(
              destinationCoordinates,
              popupstr,
              indicator,
              map?.current,
              "left"
            );
          } else {
            console.log("No valid coordinates for destination location");
          }

          if (validateCoord(coords)) {
            //current location popup
            //if shipment is completed we should not show current location and dotted lines
            if (shipmentData?.shipmentstate?.toLowerCase() != "completed") {
              const currentLoc = coords;
              const statusClass =
                shipmentData?.status == "OK"
                  ? "no-alert"
                  : shipmentData?.status == "ERROR"
                  ? "critical"
                  : shipmentData?.status;
              const status =
                shipmentData?.status === "ERROR"
                  ? "CRITICAL"
                  : shipmentData?.status;

              getAddressfromLatLong(currentLoc[1], currentLoc[0]).then(
                (address) => {
                  const popupstr = returnPopups({
                    status,
                    state: shipmentData?.shipmentstate,
                    timestamp: shipmentData?.updatedat,
                    Address: address,
                    statusClass,
                  });
                  const indicator = `<img src="/assets/stateIndicator.svg" style="width:41px; height:60px; rotate: 294deg;" alt="Current Marker"/>`;
                  const { popup, indicator: markerReturned } =
                    popUpAndIndicator(
                      currentLoc,
                      popupstr,
                      indicator,
                      map?.current,
                      "bottom"
                    );
                  markRef.current = markerReturned;

                popup.on("open", () => {
                  document
                    .getElementById("statusClickView")
                    ?.addEventListener("click", (e) => {
                      if (e?.target?.innerHTML?.trim() === "Ok" || e?.target?.innerHTML?.trim() === "Completed") return;
                      if (e.target.id?.trim() === "statusClickView") {
                        dispatch(setActiveTabGlobal({ activeTabGlobal: "dashboard" }));
                        dispatch(setShipmentDataGlobal(shipmentData));
                        dispatch(setShipmentStatusGlobal(e.target.innerHTML.trim()));
                        navigate(`/viewshipment/${shipmentData?.shipmentid}`);
                        return;
                      }
                    });
                });
              });

              // Adding Dotted line
              map.current.addSource("dotted-line-sourcel", {
                type: "geojson",
                data: {
                  type: "FeatureCollection",
                  features: [
                    {
                      type: "Feature",
                      geometry: {
                        type: "LineString",
                        coordinates: [currentLoc, destinationCoordinates], // single vector array
                      },
                      properties: {},
                    },
                  ],
                },
              });
              map.current.addLayer({
                id: "dotted-line-layerl",
                type: "line",
                source: "dotted-line-sourcel",
                layout: {
                  "line-join": "round",
                  "line-cap": "round",
                },
                paint: {
                  "line-color": "#000000",
                  "line-width": 2,
                  "line-dasharray": [1, 2],
                },
              });
              addAccuracyCircle(coords, accuracy, map.current, bounds);
            }
          }
        });
      }
    } else {
      const originCoordinates =
        Array.isArray(mapData?.originCoordinates) &&
        mapData.originCoordinates.length === 2 &&
        !mapData.originCoordinates.some(isNaN)
          ? mapData.originCoordinates
          : [-100.0, 31.0];

      //  const originCoordinates = mapData?.originCoordinates || [-100.0, 31.0];

      const customStartIconMarker = new mapboxgl.Marker({
        element: document.createElement("div"),
      });
      customStartIconMarker.getElement().innerHTML = `<div class="shipment-map-indicator-container">
        <div class="shipment-map-start-indicator-info">
            <div>
                <img src="/assets/map-Indicators.svg" style="width:20px; height:20px" alt="Start Marker"/>
            </div>
            <div class="shipment-map-indicator-info-body">
                <h5>Starting Point</h5>
                <h6>ABC</h6>
            </div>
        </div>
        <img src="/assets/map-Indicators.svg" style="width:20px; height:20px" alt="Start Marker"/>
    </div>`;

      const customEndIconMarker = new mapboxgl.Marker({
        element: document.createElement("div"),
      });

      customEndIconMarker.getElement().innerHTML = `<div class="shipment-map-indicator-container">
        <div class="shipment-map-end-indicator-info">
            <div>
                <img src="/assets/destination-location.svg" style="width:20px; height:20px" alt="Start Marker"/>
            </div>
            <div class="shipment-map-indicator-info-body">
                <h5>Destination</h5>
                <h6>XYZ</h6>
            </div>
        </div>
        <img src="/assets/map-Indicators.svg" style="width:20px; height:20px" alt="End Marker"/>
    </div>`;

      const nav = new mapboxgl.NavigationControl({
        visualizePitch: true,
      });
      try {
        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: `mapbox://styles/${mapboxOwner}/${terrainId}`,
          // style: "mapbox://styles/mapbox/streets-v12",
          center: originCoordinates,
          zoom: 9,
        });
      } catch (e) {
        console.error("Failed to initialize map:", e);
        return;
      }

      // map.current.addControl(new mapboxgl.FullscreenControl(), "top-right");
      map.current.addControl(nav, "bottom-right");
      // customStartIconMarker.setLngLat(originCoordinates).addTo(map.current);
      // customEndIconMarker.setLngLat(destinationCoordinates).addTo(map.current);

      // adds sattelite and Terrain buttons to map
      addButtons(map.current, terrainId, satelliteId, mapboxOwner);

      const directions = new MapboxDirections({
        accessToken: mapboxToken,
        unit: "metric",
        profile: "mapbox/driving",
        interactive: false,
        controls: false,
      });

      const shipmentsToUse =
        shipmentsSelected?.length === 0 || shipmentsSelected === undefined
          ? shipments
          : shipmentsSelected;

      map.current.addControl(directions, "top-left");
      const addMapLayers = () => {
        if (!map.current.getSource("clusters")) {
          map.current.addSource("clusters", {
            type: "geojson",
            data: createGeoJsonSource(shipmentsToUse),
            cluster: true,
            clusterMaxZoom: 14,
            clusterRadius: 50,
            clusterProperties: {
              has_critical: [
                "max",
                ["case", ["==", ["get", "status"], "ERROR"], 1, 0],
              ],
              has_warning: [
                "max",
                ["case", ["==", ["get", "status"], "WARNING"], 1, 0],
              ],
              has_notification: [
                "max",
                ["case", ["==", ["get", "status"], "NOTIFICATION"], 1, 0],
              ],
              has_completed: [
                "max",
                ["case", ["==", ["get", "status"], "COMPLETED"], 1, 0],
              ],
              has_noAlert: [
                "max",
                ["case", ["==", ["get", "status"], "OK"], 1, 0],
              ],
            },
          });

          map.current.addLayer({
            id: "clusters",
            type: "circle",
            source: "clusters",
            filter: ["has", "point_count"],
            paint: {
              "circle-color": [
                "case",
                ["==", ["get", "has_critical"], 1],
                "#ff8027",
                ["==", ["get", "has_warning"], 1],
                "#fed60e",
                ["==", ["get", "has_notification"], 1],
                "#006FC1",
                ["==", ["get", "has_completed"], 1],
                "#AAD8F9",
                "#00B845",
              ],
              "circle-radius": 20,
            },
          });

          map.current.addLayer({
            id: "cluster-count",
            type: "symbol",
            source: "clusters",
            filter: ["has", "point_count"],
            layout: {
              "text-field": "{point_count_abbreviated}",
              "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
              "text-size": 12,
              "text-offset": [0, 0],
            },
          });

          map.current.addLayer({
            id: "unclustered-point",
            type: "circle",
            source: "clusters",
            filter: ["!", ["has", "point_count"]],
            paint: {
              "circle-color": [
                "case",
                ["==", ["get", "status"], "ERROR"],
                "#ff8027",
                ["==", ["get", "status"], "WARNING"],
                "#fed60e",
                ["==", ["get", "status"], "NOTIFICATION"],
                "#006FC1",
                ["==", ["get", "status"], "COMPLETED"],
                "#AAD8F9",
                "#00B845",
              ],
              "circle-radius": 15,
              "circle-stroke-width": 1,
              "circle-stroke-color": "#fff",
            },
          });

          map.current.addLayer({
            id: "unclustered-point-label",
            type: "symbol",
            source: "clusters",
            filter: ["!", ["has", "point_count"]],
            layout: {
              "text-field": "{id}",
              "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
              "text-size": 12,
            },
            paint: {
              "text-color": "#fff",
            },
          });

          //removes popups
          const removePopups = () => {
            const popups = document.getElementsByClassName("mapboxgl-popup");
            if (popups.length > 0) {
              for (const popup of popups) {
                popup.remove();
              }
            }
          };

          map.current?.on("mouseenter", "unclustered-point", () => {
            map.current.getCanvas().style.cursor = "pointer";
          });
          map.current?.on("mouseenter", "clusters", () => {
            map.current.getCanvas().style.cursor = "pointer";
          });
          map.current?.on("mouseleave", "unclustered-point", () => {
            map.current.getCanvas().style.cursor = "";
          });
          map.current?.on("mouseleave", "clusters", () => {
            map.current.getCanvas().style.cursor = "";
          });

          const fitPopupToView = (coords, offsetx = 110) => {
            map.current.easeTo({
              center: coords,
              offset: [0, offsetx],
              duration: 500,
            });
          };

          const unclusterPopup = async (e) => {
            const coordinates = e.features[0].geometry.coordinates.slice();
            const { title, value } = e.features[0].properties;
            const valueJson = JSON.parse(value);
            let status = valueJson?.status || valueJson?.record?.status;
            status = status === "ERROR" ? "CRITICAL" : status;
            const statusClass =
              status == "OK"
                ? "no-alert"
                : status == "ERROR"
                ? "critical"
                : status;
            const state =
              valueJson?.record?.state || valueJson?.state || "Unknown";
            const shipmentid =
              valueJson?.record?.shipmentid ||
              valueJson?.shipmentid ||
              "Unknown";
            const address = await getAddressfromLatLong(
              coordinates[1],
              coordinates[0]
            );
            const popupStr = returnPopups({
              status,
              state,
              locationName: title,
              Address: address,
              statusClass,
              noNeed: false,
              statusid: "statusClickNonCluster",
            });
            const popup = new mapboxgl.Popup({
              closeButton: true,
              closeOnClick: true,
              className: "shipment-popup",
            })
              .setLngLat(coordinates)
              .setHTML(popupStr);
            popup?.on('open', () => {
              document.getElementById('statusClickNonCluster')?.addEventListener('click', (e) => {
                if (e?.target?.innerHTML.trim() === "Ok" || e?.target?.innerHTML?.trim() === "Completed") return;
                if (e.target.id === 'statusClickNonCluster') {
                  dispatch(setActiveTabGlobal({ activeTabGlobal: 'dashboard' }))
                  dispatch(setShipmentDataGlobal(valueJson?.record ? valueJson?.record : valueJson));
                  dispatch(setShipmentStatusGlobal(e.target.innerHTML.trim()))
                  navigate(`/viewshipment/${shipmentid}`);
                  return;
                }
              })
            })
            popup.addTo(map.current);
            fitPopupToView(coordinates);
            return popup;
          };

          map.current.on("click", "unclustered-point", async (e) => {
            const { value } = e.features[0].properties;
            const parsedvalue = JSON.parse(value);
            dispatch(
              updateFilter({
                key: "shipmentid",
                value: {
                  option: Object.hasOwn(parsedvalue, "shipmentid") ? [parsedvalue?.shipmentid]:Object.hasOwn(parsedvalue.record, "shipmentid") ? [parsedvalue?.record?.shipmentid] : [],
                  sort:  "asc",
                },
              })
            );
            const popup = await unclusterPopup(e);
            popup?.on("close", () => {
              dispatch(setShipmentTitlesForFilter([]));
            });
          });

          map.current.on("mouseover", "unclustered-point", async (e) => {
            removePopups();
            const { value } = e.features[0].properties;
            // dispatch(setShipmentTitlesForHighlight([JSON.parse(value)?.shipmentid]));
            dispatch(setShipmentTitlesForHighlight([]));
            const popup = await unclusterPopup(e);
            popup?.on("close", () => {
              dispatch(setShipmentTitlesForHighlight([]));
            });
          });

          map.current?.on("zoom", () => {
            removePopups();
          });

          const clusterPopup = async (e) => {
            const clusterId = e?.features[0]?.properties?.cluster_id;
            const coordinates = e.features[0].geometry.coordinates.slice();
            const clusterSource = map.current?.getSource("clusters");
            const popups = [];
            let popup = null;
            let accumulator = [];
            const waitFn = async () => {
              await new Promise((resolve) => {
                clusterSource.getClusterLeaves(
                  clusterId,
                  e?.features[0]?.properties?.point_count || 0,
                  0,
                  async (err, leaves) => {
                    if (err) return;
                    for (const feature of leaves) {
                     
                      const { title, value } = feature.properties;
                      let status = value?.status || value?.record?.status;
                      status = status === "ERROR" ? "CRITICAL" : status;
                      const statusClass =
                        status == "OK"
                          ? "no-alert"
                          : status == "ERROR"
                          ? "critical"
                          : status;
                      const state =
                        value?.state || value?.record?.state || "Unknown";
                      const shipmentname = title || "Unknown";
                      const address = await getAddressfromLatLong(
                        coordinates[1],
                        coordinates[0]
                      );
                      const statusInfo = encodeURIComponent(
                        JSON.stringify(value?.record ? value?.record : value)
                      );
                      const popupStr = returnPopups({
                        status,
                        state,
                        locationName: title,
                        Address: address,
                        statusClass,
                        noNeed: false,
                        statusInfo,
                        statusCls: "statusClickCluster",
                      });
                      popups.push(
                        `<div class="mappopup-info">${popupStr}</div>`
                      );
                      accumulator.push( value?.shipmentid? value?.shipmentid : value?.record?.shipmentid);
                    }

                    let poupupHtml = popups.join(
                      `<div class="map-data-seperator"></div>`
                    );

                    popup = new mapboxgl.Popup({
                      closeButton: true,
                      closeOnClick: true,
                      className: "shipment-popup",
                    })
                      .setLngLat(coordinates)
                      .setHTML(
                        `<div class="cluster-map-popup-container">
                   ${poupupHtml}
                  </div>`).addClassName('shipmentCluster')

                  popup.on('open', () => {
                    document.querySelectorAll('.statusClickCluster')?.forEach(elem => {
                      elem?.addEventListener('click', (e) => {
                        if (e?.target?.innerHTML.trim() === "Ok" || e?.target?.innerHTML?.trim() === "Completed") return;
                        dispatch(setActiveTabGlobal({ activeTabGlobal: 'dashboard' }))
                        const data = JSON.parse(decodeURIComponent(e.target.getAttribute('data-info')));
                        dispatch(setShipmentDataGlobal(data));
                        dispatch(setShipmentStatusGlobal(e.target.innerHTML.trim()))
                        navigate(`/viewshipment/${data?.shipmentid}`);
                        return;
                      })
                    })
                  })

                    popup.addTo(map.current);
                    fitPopupToView(coordinates);
                    resolve();
                  }
                );
              }).then(() => {});
            };
            await waitFn();
            return { accumulator, popup };
          };

          map.current?.on("click", "clusters", async (e) => {
            removePopups();
            // const data = await clusterPopup(e);
            const {accumulator, popup} = await clusterPopup(e);
            dispatch(
              updateFilter({
                key: "shipmentid",
                value: {
                  option: Array.isArray(accumulator) ? accumulator : [] ,
                  sort:  "asc",
                },
              })
            );
            dispatch(setShipmentTitlesForFilter(accumulator));
            popup?.on("close", () => {
              dispatch(setShipmentTitlesForFilter([]));
            });
          });

          map.current?.on("mouseover", "clusters", async (e) => {
            removePopups();
            const { accumulator, popup } = await clusterPopup(e);
            // dispatch(setShipmentTitlesForHighlight(accumulator));
            dispatch(setShipmentTitlesForHighlight([]));
            popup?.on("close", () => {
              dispatch(setShipmentTitlesForHighlight([]));
            });
          });
        }
      };
      map.current.on("styledata", addMapLayers);
      map.current.on("load", () => {
        addMapLayers();
      });
    }
    return () => {
      map.current.remove();
    };
  }, [
    isLoadingMap,
    mapData?.destinationCoordinates,
    mapData?.originCoordinates,
    shipments,
    shipmentsSelected,
    selectedDeviceDetails,
    pathPoints,
  ]);

  const adjustOverlappedCoord = (seen, coord, magnitude = 0.0001) => {
    const coordStr = coord.join(",");
    if (seen.includes(coordStr)) {
      const adjustedCoord = [
        coord[0] + (Math.random() - 0.5) * magnitude,
        coord[1] + (Math.random() - 0.5) * magnitude,
      ];
      seen.push(adjustedCoord.join(","));
      return adjustedCoord;
    } else {
      seen.push(coordStr);
      return coord;
    }
  };

  const createGeoJsonSource = (shipmentsData) => {
    const seen = [];
    const bounds = new mapboxgl.LngLatBounds();
    const shipmentsDatatmp = shipmentsData?.map((shipment) => {
      const coord = shipment.coordinates || shipment.record?.coordinates;
      if (!validateCoord(coord)) {
        console.log(
          "invalid coordinates found while plotting clusters",
          shipment.shipmentName || shipment.record?.shipmentName,
          coord
        );
        return;
      }
      bounds.extend(coord);
      return {
        type: "Feature",
        properties: {
          id: 1,
          title: shipment.shipmentName || shipment.record?.shipmentName,
          location:
            shipment?.currentlocation || shipment?.record?.currentlocation,
          value: shipment || shipment.record,
          status: shipment.status || shipment.record?.status,
        },
        geometry: {
          type: "Point",
          coordinates:
            adjustOverlappedCoord(seen, coord) ||
            adjustOverlappedCoord(seen, coord),
        },
      };
    });
    const source = {
      type: "FeatureCollection",
      features: shipmentsDatatmp.filter((data) => data != undefined),
    };

    const sw = [bounds?._sw?.lng, bounds?._sw?.lat];
    const ne = [bounds?._ne?.lng, bounds?._ne?.lat];

    if (validateCoord(sw) && validateCoord(ne)) {
      map?.current?.fitBounds(bounds, {
        padding: 40,
        maxZoom: 15,
        duration: 1000,
      });
    }

    return source;
  };

  const handleFullScreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else fullScreenContainer.current.requestFullscreen();

    //dispatch(updateMap({isFullScreen: !isMapFullScreen}));
  };

  return (
    <div className="shipment-map-container">
      <Spin
        style={{ backgroundColor: "white" }}
        spinning={isLoadingMap || shipmentsSelected.length === 1 && !trackDataLoaded}
        tip="Loading..."
      >
        <div ref={fullScreenContainer} style={{ height: "100%" }}>
          <div className="shipment-map-fullscreen">
            <IconButton
              style={{
                position: "absolute",
                top: "3px",
                right: "3px",
                zIndex: 1111,
                backgroundColor: "#fff",
                borderRadius: "8px",
                width: "32px",
                height: "32px",
              }}
              onClick={handleFullScreen}
            >
              <img
                alt="FullScreen button"
                src={isMapFullScreen ? ExitFullscreen : Fullscreen}
                height={"16px"}
                width={"16px"}
              />
            </IconButton>
          </div>
          <div
            ref={mapContainer}
            style={
              isMapFullScreen
                ? { width: "100%", height: "100%" }
                : { width: "100%", height: "35dvh" }
            }
          ></div>
        </div>
      </Spin>
    </div>
  );
}

export default ShipmentMap;
